import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Pencil, MapPin, Loader2, X } from "lucide-react";
import { people, companies as allCompanies } from "@/data/dummy";
import { isLovablePreview } from "@/lib/utils/environment";
import { useCanMutate } from "@/lib/rbac";
import type { RestoreCellDetail } from "@/components/shared/EditableCell";

interface EditableLocationFieldProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Grid cell id (`recordId:columnKey`). When set, the field answers Field History's
   * "restoreCellValue" like EditableCell does, saving through this same `onChange`.
   */
  cellId?: string;
  icon?: React.ReactNode;
  className?: string;
  hideIcon?: boolean;
  compact?: boolean;
}

// Common US locations as fallback when Google API is unavailable
const COMMON_LOCATIONS = [
  "San Francisco, CA",
  "New York, NY",
  "Los Angeles, CA",
  "Chicago, IL",
  "Austin, TX",
  "Seattle, WA",
  "Boston, MA",
  "Denver, CO",
  "Atlanta, GA",
  "Miami, FL",
  "Portland, OR",
  "San Diego, CA",
  "Dallas, TX",
  "Philadelphia, PA",
  "Phoenix, AZ",
];

// Build a static list of known locations from dummy data + common locations
const knownLocations = [...new Set([
  ...people.map(p => p.location).filter(Boolean),
  ...allCompanies.map(c => c.location).filter(Boolean),
  ...COMMON_LOCATIONS,
])] as string[];

// Google Maps loading
let googleMapsLoaded = false;
let googleMapsLoadPromise: Promise<void> | null = null;

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (googleMapsLoaded) return Promise.resolve();
  if (googleMapsLoadPromise) return googleMapsLoadPromise;
  if ((window as any).google?.maps?.places) { googleMapsLoaded = true; return Promise.resolve(); }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => { googleMapsLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return googleMapsLoadPromise;
}

export default function EditableLocationField({
  value,
  onChange,
  cellId,
  icon,
  className,
  hideIcon,
  compact,
}: EditableLocationFieldProps) {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const [editing, setEditingState] = useState(false);
  const setEditing = (next: boolean) => {
    if (next && !canMutate) return;
    setEditingState(next);
  };
  const [searchText, setSearchText] = useState("");

  // Programmatic restore from Field History (mirrors EditableCell): a probe only asks whether
  // this cell would take it; a real restore saves through the same onChange as a manual edit.
  useEffect(() => {
    if (!cellId || !canMutate) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<RestoreCellDetail>).detail;
      if (detail?.cellId !== cellId || detail.handled) return;
      detail.handled = true;
      if (detail.probe) return;
      if (detail.value !== value) onChange(detail.value);
    };
    window.addEventListener("restoreCellValue", handler);
    return () => window.removeEventListener("restoreCellValue", handler);
  }, [cellId, canMutate, value, onChange]);
  const [googleLoaded, setGoogleLoaded] = useState(googleMapsLoaded);
  const [googlePredictions, setGooglePredictions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);

  // Update dropdown position when editing — track scroll and resize
  useEffect(() => {
    if (!editing || !inputRef.current) return;
    const updatePos = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) {
        setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [editing]);

  // Try loading Google Maps in background (non-blocking)
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    // If already available (e.g. loaded by another script), init immediately
    if ((window as any).google?.maps?.places) {
      googleMapsLoaded = true;
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      placesService.current = new (window as any).google.maps.places.PlacesService(div);
      setGoogleLoaded(true);
      return;
    }

    loadGoogleMapsScript(apiKey)
      .then(() => {
        setGoogleLoaded(true);
        autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
        const div = document.createElement('div');
        placesService.current = new (window as any).google.maps.places.PlacesService(div);
      })
      .catch(() => {}); // Silently fail - we have local suggestions
  }, []);

  // Prefill only when editing STARTS. `value` is read from a ref so that a
  // mid-edit prop change (e.g. the detail sheet swapping grid-row data for the
  // slower backend result) can't clobber the user's in-progress search text.
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (editing) {
      setSearchText(valueRef.current || "");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing]);

  // Close on outside click — check both container and dropdown refs
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setEditing(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editing]);

  // Local filtered suggestions (immediate, no API needed)
  const localSuggestions = useMemo(() => {
    if (!searchText.trim()) return knownLocations.slice(0, 8);
    const lower = searchText.toLowerCase();
    return knownLocations.filter(loc => loc.toLowerCase().includes(lower)).slice(0, 8);
  }, [searchText]);

  // Google predictions (async enhancement)
  const fetchGooglePredictions = useCallback((input: string) => {
    // Lazy init in case useEffect's promise got stuck but the API is now available
    if (!autocompleteService.current && (window as any).google?.maps?.places) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current || !input.trim()) {
      setGooglePredictions([]);
      return;
    }
    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      { input, types: ["country", "administrative_area_level_1", "administrative_area_level_2", "administrative_area_level_3", "locality"] },
      (results: any, status: any) => {
        setLoading(false);
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && results) {
          setGooglePredictions(results);
        } else {
          setGooglePredictions([]);
        }
      }
    );
  }, [googleLoaded]);

  // Debounced Google search
  useEffect(() => {
    if (!searchText.trim()) { setGooglePredictions([]); return; }
    const timer = setTimeout(() => fetchGooglePredictions(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText, fetchGooglePredictions]);

  const extractLocationFromPlace = (placeDetails: any): string => {
    let city = "", state = "", country = "";
    placeDetails.address_components?.forEach((component: any) => {
      const types = component.types;
      if (types.includes("locality")) city = component.long_name;
      else if (types.includes("administrative_area_level_1")) state = component.long_name;
      else if (types.includes("country")) country = component.long_name;
    });
    return [city, state, country].filter(Boolean).join(", ");
  };

  const onGoogleOptionSelected = (prediction: any) => {
    // Lazy init in case useEffect's promise got stuck but the API is now available
    if (!placesService.current && (window as any).google?.maps?.places) {
      const div = document.createElement('div');
      placesService.current = new (window as any).google.maps.places.PlacesService(div);
    }
    if (!placesService.current) return;
    placesService.current.getDetails(
      { placeId: prediction.place_id, fields: ["address_components"] },
      (placeDetails: any, status: any) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && placeDetails) {
          const location = extractLocationFromPlace(placeDetails);
          onChange(location);
          setEditing(false);
          setSearchText("");
        }
      }
    );
  };

  const onLocalOptionSelected = (location: string) => {
    onChange(location);
    setEditing(false);
    setSearchText("");
  };

  // In production, only show Google Places results (properly formatted long_name).
  // Local suggestions (short format) are only used in preview mode as a fallback.
  const preview = isLovablePreview();
  const allSuggestions = useMemo(() => {
    if (preview) {
      return { local: localSuggestions, google: [] as any[] };
    }
    return { local: [] as string[], google: googlePredictions };
  }, [localSuggestions, googlePredictions, preview]);

  const hasAnySuggestions = allSuggestions.local.length > 0 || allSuggestions.google.length > 0;

  if (editing) {
    return (
      <div className={cn("flex items-center relative", compact ? "gap-2 text-xs leading-5" : "gap-3 text-sm", className)} ref={containerRef}>
        {!hideIcon && <span className="shrink-0">{icon || <MapPin className="h-3.5 w-3.5 text-muted-foreground" />}</span>}
        <div className="flex-1 min-w-0 relative">
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEditing(false);
                setSearchText("");
              }
              if (e.key === "Enter") {
                onChange(searchText.trim());
                setEditing(false);
                setSearchText("");
              }
            }}
            placeholder={t("grid.cell.searchLocation")}
            className={cn(
              // Reserve space on the right so the absolutely-positioned Clear (X)
              // button doesn't overlap the location text as it grows.
              "w-full bg-transparent outline-none py-0 pl-0 pr-6",
              compact ? "text-xs border-0" : "text-sm border-b border-accent",
            )}
          />
          <div className="absolute right-0 top-0 flex items-center gap-1">
            {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            {searchText && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange("");
                  setEditing(false);
                  setSearchText("");
                }}
                className="text-muted-foreground hover:text-foreground"
                title={t("grid.cell.clearLocation")}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {hasAnySuggestions && dropdownPos && (
            <div
              ref={dropdownRef}
              className="fixed z-[100] rounded-md border border-border bg-popover shadow-md py-1 max-h-48 overflow-y-auto whitespace-normal"
              style={{ top: dropdownPos.top, left: dropdownPos.left, width: Math.max(dropdownPos.width, 260) }}
            >
              {allSuggestions.local.map((loc) => (
                <button
                  key={loc}
                  onMouseDown={(e) => { e.preventDefault(); onLocalOptionSelected(loc); }}
                  className={cn("w-full text-left transition", compact ? "px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground" : "px-3 py-2 text-sm hover:bg-muted/50")}
                >
                  {loc}
                </button>
              ))}
              {allSuggestions.google.map((prediction) => (
                <button
                  key={prediction.place_id}
                  onMouseDown={(e) => { e.preventDefault(); onGoogleOptionSelected(prediction); }}
                  className={cn("w-full text-left transition", compact ? "px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground" : "px-3 py-2 text-sm hover:bg-muted/50")}
                >
                  {prediction.description}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!canMutate) {
    return (
      <div className={cn("flex items-center", compact ? "gap-2 text-xs leading-5" : "gap-3 text-sm", className)} title={t("common.readOnlyCannotChange")}>
        {!hideIcon && <span className="shrink-0">{icon || <MapPin className="h-3.5 w-3.5 text-muted-foreground" />}</span>}
        <span className={cn("flex-1 min-w-0 truncate", !value && "text-muted-foreground")}>
          {value || ""}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center group", compact ? "gap-2 text-xs leading-5" : "gap-3 text-sm", className)}>
      {!hideIcon && <span className="shrink-0">{icon || <MapPin className="h-3.5 w-3.5 text-muted-foreground" />}</span>}
      <button
        onClick={() => setEditing(true)}
        className={cn("flex-1 min-w-0 text-left rounded px-1 -mx-1 transition flex items-center gap-2", compact ? "h-5 hover:bg-muted/30" : "hover:bg-muted/50")}
      >
        <span className={cn("truncate", !value && "text-muted-foreground")}>
          {value || ""}
        </span>
        <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0" />
      </button>
    </div>
  );
}
